import { ActionIcon, Button, Card, Group, Modal, Select, Stack, Table, Text, TextInput, Tooltip } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorBox, notifyOk } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { del, formatDate, get, post, type Project } from "../lib/api";
import { useMe, useRegistry } from "../lib/hooks";
import { displayProductName, MODULES } from "../lib/modules";
import { ModuleBadge } from "../components/Modules";

const order = (product: string) => {
  const i = MODULES.findIndex((m) => m.product === product || m.subProducts?.some((s) => s.product === product));
  return i < 0 ? 99 : i + (MODULES[i].product === product ? 0 : 0.5);
};

/** A product's name as shown (the module's display name: "skfitter" -> "Tight Binding"). */
export function productName(key: string, products?: { key: string; name: string }[]) {
  return displayProductName(key, products);
}

/** "New project": with `product` (a module's home page) the project is of that module; without it, a module is chosen. */
export function NewProjectButton({ product, label }: { product?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const { data: me } = useMe();
  const allowed = !product || !me || !!me.limits[`${product}.enabled`];
  return (
    <>
      <Tooltip label="Not part of your plan" disabled={allowed}>
        <Button onClick={() => setOpen(true)} disabled={!allowed}>{label ?? "New project"}</Button>
      </Tooltip>
      <NewProjectModal opened={open} onClose={() => setOpen(false)} fixed={product} />
    </>
  );
}

function NewProjectModal({ opened, onClose, fixed }: { opened: boolean; onClose: () => void; fixed?: string }) {
  const { data: reg } = useRegistry();
  const { data: me } = useMe();
  const [name, setName] = useState("");
  const [chosen, setProduct] = useState<string | null>("transport");
  const product = fixed ?? chosen;
  const qc = useQueryClient();
  const nav = useNavigate();
  const m = useMutation({
    mutationFn: () => post<Project>("/projects", { name: name.trim(), product }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      onClose();
      setName("");
      nav(`/projects/${p.id}`);
    },
  });
  const options = (reg?.products ?? [])
    .map((p) => ({ value: p.key, label: productName(p.key, reg?.products), disabled: me ? !me.limits[`${p.key}.enabled`] : false }))
    .sort((a, b) => order(a.value) - order(b.value));
  const title = fixed ? `New ${productName(fixed, reg?.products)} project` : "New project";
  return (
    <Modal opened={opened} onClose={() => { m.reset(); onClose(); }} title={title} centered>
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <Stack>
          <TextInput label="Name" required maxLength={120} value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          {!fixed && <Select label="Module" data={options} value={product} onChange={setProduct} allowDeselect={false} />}
          <ErrorBox error={m.error} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={m.isPending} disabled={!name.trim() || !product}>Create</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/** Delete a project and all its files, after a confirmation (the same as "More → Delete project" on its page). */
function DeleteProjectModal({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: string) => del<{ deleted_files: number }>(`/projects/${id}`),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      notifyOk(`Project "${project?.name}" deleted${r.deleted_files ? ` with ${r.deleted_files} file(s)` : ""}.`);
      onClose();
    },
  });
  const n = project?.file_count ?? 0;
  return (
    <Modal opened={!!project} onClose={() => { m.reset(); onClose(); }} title={project ? `Delete "${project.name}"?` : ""} centered>
      <Text size="sm">
        This deletes the project and {n === 1 ? "its file" : `its ${n} files`} (uploads and results) for good.
        Calculations stay in your job list. To keep anything, open the project and use "Download as zip" first.
      </Text>
      <ErrorBox error={m.error} />
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>Keep</Button>
        <Button color="red" loading={m.isPending} onClick={() => project && m.mutate(project.id)}>Delete</Button>
      </Group>
    </Modal>
  );
}

export function Projects() {
  const { data, isLoading } = useQuery({ queryKey: ["projects"], queryFn: () => get<Project[]>("/projects") });
  const { data: me } = useMe();
  const [deleting, setDeleting] = useState<Project | null>(null);
  return (
    <>
      <PageTitle title="Projects">
        <NewProjectButton />
      </PageTitle>
      {me && (
        <Text size="sm" c="dimmed" mb="sm">
          {me.usage.projects} of {String(me.limits["storage.max_projects"])} projects in your plan.
        </Text>
      )}
      <Card withBorder>
        {isLoading ? <Text c="dimmed">Loading…</Text> : !data?.length ? (
          <Text c="dimmed">No projects yet. Create one to upload data and run calculations.</Text>
        ) : (
          <Table.ScrollContainer minWidth={480}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr><Table.Th>Name</Table.Th><Table.Th>Module</Table.Th><Table.Th>Created</Table.Th><Table.Th>Updated</Table.Th><Table.Th w={40} /></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td><Link to={`/projects/${p.id}`}>{p.name}</Link></Table.Td>
                    <Table.Td><ModuleBadge product={p.product} /></Table.Td>
                    <Table.Td>{formatDate(p.created_at)}</Table.Td>
                    <Table.Td>{formatDate(p.updated_at)}</Table.Td>
                    <Table.Td>
                      <Tooltip label="Delete project">
                        <ActionIcon variant="subtle" color="red" aria-label={`Delete project ${p.name}`} onClick={() => setDeleting(p)}>✕</ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
      <DeleteProjectModal project={deleting} onClose={() => setDeleting(null)} />
    </>
  );
}
